import { useEffect, useState, useRef } from "react";
import { subscribeToUserPatients, countUserPatients, searchUserPatients } from '../services/patientService';
import { getOfflinePatients } from '../services/offlineService';
import { CssBaseline, Box } from "@mui/material";
import PatientsDataGrid from './components/patients-data-grid.jsx';
import AppTheme from "../shared-theme/AppTheme";
import {
  chartsCustomizations,
  dataGridCustomizations,
  // datePickersCustomizations,
  treeViewCustomizations,
} from "../dashboard/theme/customizations";
import { useAuth } from "../shared/context/AuthContext";
import SideMenu from "../shared/components/SideMenu";
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PeopleOutlineRoundedIcon from '@mui/icons-material/PeopleOutlineRounded';
import { useNavigate } from "react-router-dom";
import BreadCrumbsWithIcons from "../shared/components/BreadCrumbsWithIcons";

const xThemeComponents = {
  ...chartsCustomizations,
  ...dataGridCustomizations,
  // ...datePickersCustomizations,
  ...treeViewCustomizations,
};

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_LENGTH = 2;

/** @param {{ patientId: string, displayName: string, createdTime?: number, examDate?: string | null }} p */
function toGridRow(p) {
  return { id: p.patientId, name: p.displayName, createdTime: p.createdTime ?? 0, examDate: p.examDate ?? null };
}

/** @param {any} props */
export default function Patients(props) {
  const { auth, user, isDemo, isOffline, isAuthResolved } = useAuth();
  const isDemoOffline = isDemo && isOffline;
  const navigate = useNavigate();
  const [isOnline, setIsOnline] = useState(typeof navigator !== 'undefined' ? navigator.onLine : true);

  const [authChecked, setAuthChecked] = useState(false);
  const uidToUseRef = useRef(/** @type {string | null} */ (null));
  const patientsUnsubscribeRef = useRef(/** @type {null | (() => void)} */ (null));

  // Paginated / offline browse state
  const [rows, setRows] = useState(/** @type {{ id: string, name: string, createdTime: number, examDate: string | null }[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rowCount, setRowCount] = useState(0);
  // cursorsRef.current[i] is the Firestore cursor to fetch page i (index 0 = null = first page)
  const cursorsRef = useRef(/** @type {Array<any>} */ ([null]));

  // Server search state
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTruncated, setSearchTruncated] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [searchRows, setSearchRows] = useState(/** @type {{ id: string, name: string, createdTime: number, examDate: string | null }[]} */ ([]));

  const isSearching = debouncedSearchTerm.trim().length >= SEARCH_MIN_LENGTH;

  // Track browser online/offline transitions
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Debounce the raw search input before it drives any network call.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchTerm(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Auth effect: detect user + claims, reset pagination/search state per user.
  useEffect(() => {
    if (!isAuthResolved) return;

    if (patientsUnsubscribeRef.current) {
      patientsUnsubscribeRef.current();
      patientsUnsubscribeRef.current = null;
    }

    if (user) {
      const uidToUse = isDemo ? 'demo' : user.uid;
      uidToUseRef.current = uidToUse;
      cursorsRef.current = [null];
      setPage(0);
      setSearchInput('');
      setDebouncedSearchTerm('');
      setRowCount(0);
      setLoading(true);

      if (!isDemoOffline) {
        countUserPatients(uidToUse)
          .then(setRowCount)
          .catch((err) => console.error('countUserPatients error', err));
      }
    } else {
      uidToUseRef.current = null;
      navigate('/login', { replace: true });
      setLoading(false);
    }

    setAuthChecked(true);

    return () => {
      if (patientsUnsubscribeRef.current) {
        patientsUnsubscribeRef.current();
        patientsUnsubscribeRef.current = null;
      }
    };
  }, [isAuthResolved, user, isDemo, isDemoOffline, navigate]);

  // Paginated (online) / IndexedDB (offline) data loading - skipped while a search is active.
  useEffect(() => {

    if (patientsUnsubscribeRef.current) {
      patientsUnsubscribeRef.current();
      patientsUnsubscribeRef.current = null;
    }

    if (!authChecked || isSearching) return;
    const uid = uidToUseRef.current;
    if (!uid) return;

    let cancelled = false;

    if (isDemoOffline && !isOnline) {
      setLoading(true);
      setLoadError(false);
      getOfflinePatients()
        .then((patients) => {
          if (cancelled) return;
          const mapped = patients.map((p) => toGridRow({ patientId: p.patientId, displayName: p.displayName, createdTime: p.createdTime }));
          setRows(mapped);
          setRowCount(mapped.length);
          setLoading(false);
        })
        .catch((err) => {
          if (cancelled) return;
          console.error('failed to load offline patients', err);
          setLoadError(true);
          setLoading(false);
        });
      return () => { cancelled = true; };
    }

    if (cursorsRef.current[page] === undefined) return;

    setLoading(true);
    setLoadError(false);
    patientsUnsubscribeRef.current = subscribeToUserPatients(
      uid,
      { pageSize, cursor: cursorsRef.current[page] },
      ({ rows: pageRows, lastDoc }) => {
        if (cancelled) return;
        setRows(pageRows.map(toGridRow));
        if (lastDoc) cursorsRef.current[page + 1] = lastDoc;
        setLoading(false);
      },
      (err) => {
        if (cancelled) return;
        console.error('subscribeToUserPatients error', err);
        setLoadError(true);
        setLoading(false);
      }
    );

    return () => { cancelled = true; };
  }, [authChecked, isSearching, isDemoOffline, isOnline, page, pageSize]);

  // Server-side search - server-only, so it is unavailable in the demo-offline-while-offline state.
  useEffect(() => {
    if (!isSearching) return;
    const uid = uidToUseRef.current;
    if (!uid) return;

    if (isDemoOffline && !isOnline) {
      setSearchLoading(false);
      setSearchError(true);
      return;
    }

    let cancelled = false;
    setSearchLoading(true);
    setSearchError(false);

    searchUserPatients(debouncedSearchTerm.trim())
      .then(({ rows: results, truncated }) => {
        if (cancelled) return;
        setSearchRows(results.map(toGridRow));
        setSearchTruncated(truncated);
        setSearchLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('searchUserPatients error', err);
        setSearchError(true);
        setSearchLoading(false);
      });

    return () => { cancelled = true; };
  }, [isSearching, debouncedSearchTerm, isDemoOffline, isOnline]);

  const handlePaginationModelChange = ({ page: newPage, pageSize: newPageSize }) => {
    if (newPageSize !== pageSize) {
      cursorsRef.current = [null];
      setPageSize(newPageSize);
      setPage(0);
      return;
    }
    if (cursorsRef.current[newPage] === undefined) return;
    setPage(newPage);
  };

  if (!authChecked) return null;

  const displayedRows = isSearching ? searchRows : rows;

  return (
    <AppTheme themeComponents={xThemeComponents} {...props}>
      <CssBaseline enableColorScheme />
      <Box sx={{ display: "flex" }}>
        <SideMenu />
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <BreadCrumbsWithIcons
            items={/** @type {any} */ ([
              { key: 'home', label: 'Home', to: '/dashboard', Icon: HomeOutlinedIcon },
              { key: 'patients', label: 'Patients', to: null, Icon: PeopleOutlineRoundedIcon },
            ])}
          />
          <PatientsDataGrid
            rows={displayedRows}
            loading={isSearching ? searchLoading : loading}
            error={isSearching ? searchError : loadError}
            isDemo={isDemo}
            isDemoOffline={isDemoOffline}
            paginationMode={isSearching ? 'client' : 'server'}
            rowCount={isSearching ? displayedRows.length : rowCount}
            paginationModel={{ page: isSearching ? 0 : page, pageSize }}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPaginationModelChange={handlePaginationModelChange}
            searchTerm={searchInput}
            onSearchTermChange={setSearchInput}
            isSearching={isSearching}
            searchTruncated={searchTruncated}
          />
        </Box>
      </Box>
    </AppTheme>
  );
}
